import { createHmac, timingSafeEqual } from "node:crypto";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { useMemo, useState } from "react";
import { MarketAnnouncementBar } from "@/components/market/MarketAnnouncementBar";
import { MarketStoreHeader } from "@/components/market/MarketChrome";
import { MarketFooter } from "@/components/market/MarketFooter";
import { MarketBottomNav } from "@/components/market/MarketBottomNav";
import { StorefrontRuntime } from "@/components/market/runtime/StorefrontRuntime";
import type { StorefrontRuntimeContext } from "@/components/market/runtime/types";
import { projectExperience } from "@/lib/experience-definition/project";
import type { TenantExperienceDocument } from "@/lib/experience-definition/types";
import { asDocument } from "./api.tenant.experience";
import { dbSmGetTenant, dbSmGetTenantConfig } from "@/lib/db-service";
import { loadMarketCatalog, type MarketCatalogData, type MarketProduct } from "@/lib/market-catalog";
import { cartLineKey } from "@/lib/market-cart-storage";
import { DEFAULT_PRODUCT_SORT, sortProducts, type ProductSortKey } from "@/lib/product-sort";
import { landingPath } from "@/lib/market-landing";
import { DEFAULT_CATEGORY_EXPERIENCE, filterCategoriesForNav } from "@/lib/category-experience";
import { slugifyName } from "@/lib/slug";
import { cn } from "@/lib/utils";

type PreviewPayload = { tenantId: string; slug: string; exp: number };

function decodeBase64Url(value: string): Buffer {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - value.length % 4) % 4);
  return Buffer.from(normalized, "base64");
}

function verifyPreviewToken(token: string, secret: string): PreviewPayload | null {
  const parts = token.split(".");
  if (parts.length !== 2) return null;
  const [encoded, signature] = parts;
  const expected = createHmac("sha256", secret).update(encoded).digest();
  const actual = decodeBase64Url(signature);
  if (actual.length !== expected.length || !timingSafeEqual(actual, expected)) return null;
  try {
    const payload = JSON.parse(decodeBase64Url(encoded).toString("utf8")) as PreviewPayload;
    if (!payload.tenantId || !payload.slug || !Number.isFinite(payload.exp)) return null;
    if (payload.exp <= Math.floor(Date.now() / 1000)) return null;
    return payload;
  } catch {
    return null;
  }
}

const loadPreview = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string; token: string }) => data)
  .handler(async ({ data }) => {
    const secret = process.env.EXPERIENCE_PREVIEW_SECRET;
    if (!secret) throw new Response("Preview no configurado", { status: 403 });
    const payload = verifyPreviewToken(data.token, secret);
    if (!payload || payload.slug !== data.slug) throw new Response("Preview no autorizado", { status: 403 });

    const tenant = await dbSmGetTenant(payload.tenantId);
    const config = await dbSmGetTenantConfig(payload.tenantId);
    if (!tenant || !config || tenant.slug !== data.slug) throw new Response("Preview no autorizado", { status: 403 });

    const doc = asDocument(config.experienceDraft);
    if (!doc) throw new Response("No hay borrador de Experience válido", { status: 404 });

    const request = getRequest();
    const catalogResult = await loadMarketCatalog(data.slug, { request });
    if (!catalogResult.ok) throw new Response(catalogResult.error, { status: catalogResult.status });

    return { doc, catalog: catalogResult.data };
  });

export const Route = createFileRoute("/market_/$slug/preview")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  loader: ({ params, search }) => loadPreview({ data: { slug: params.slug, token: search.token } }),
  component: ExperiencePreviewPage,
});

type PreviewCartLine = { product: MarketProduct; qty: number; variantId?: string };

function ExperiencePreviewPage() {
  const { doc, catalog } = Route.useLoaderData() as { doc: TenantExperienceDocument; catalog: MarketCatalogData };
  const projection = useMemo(() => projectExperience(doc), [doc]);
  const [search, setSearch] = useState("");
  const [selectedCat, setSelectedCat] = useState<string | null>(null);
  const [productSort, setProductSort] = useState<ProductSortKey>(DEFAULT_PRODUCT_SORT);
  const [cart, setCart] = useState<PreviewCartLine[]>([]);
  const [bottomTab, setBottomTab] = useState<"home" | "search" | "categories" | "cart">("home");

  const products = catalog.products;
  const filteredProducts = useMemo(() => {
    let list = products;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => p.name.toLowerCase().includes(q) || (p.description ?? "").toLowerCase().includes(q) || p.tags.some((t) => t.toLowerCase().includes(q)));
    }
    if (selectedCat) list = list.filter((p) => p.categoriaId === selectedCat);
    return sortProducts(list, productSort);
  }, [products, search, selectedCat, productSort]);

  const hasActiveFilters = Boolean(search.trim() || selectedCat);
  const cartCount = cart.reduce((sum, line) => sum + line.qty, 0);
  const cartTotal = cart.reduce((sum, line) => {
    const unit = line.product.tipoVenta === "peso" ? line.product.precioPorKg ?? 0 : line.product.precioUnitario ?? 0;
    return sum + unit * line.qty;
  }, 0);

  const addToCart = (product: MarketProduct, qty = 1) => {
    setCart((prev) => {
      const key = cartLineKey({ product });
      const existing = prev.findIndex((line) => cartLineKey(line) === key);
      if (existing < 0) return [...prev, { product, qty }];
      const next = [...prev];
      next[existing] = { ...next[existing], qty: next[existing].qty + qty };
      return next;
    });
  };

  const qtyInCart = (productId: string) => cart.filter((line) => line.product.id === productId).reduce((sum, line) => sum + line.qty, 0);
  const updateQty = (lineKey: string, delta: number) => setCart((prev) => prev.map((line) => cartLineKey(line) === lineKey ? { ...line, qty: line.qty + delta } : line).filter((line) => line.qty > 0));
  const baseLineKey = (product: MarketProduct) => cartLineKey({ product });
  const requiresDetail = (product: MarketProduct) => !doc.commercePresentation.quickAdd || (product.variants?.length ?? 0) > 0 || (product.variantAxes?.length ?? 0) > 0;
  const mainLocation = catalog.locations.find((location) => location.isDefault) ?? catalog.locations[0];

  const heroSlides = projection.hero.slides.map((slide) => ({
    id: slide.id,
    imageUrl: slide.media?.kind === "image" ? slide.media.src : null,
    videoUrl: slide.media?.kind === "video" ? slide.media.src : null,
    title: slide.headline,
    subtitle: slide.subheadline ?? null,
    ctaLabel: slide.cta1?.label,
    cta2Label: slide.cta2?.label,
    overlayOpacity: slide.overlay.opacity,
    overlayColor: slide.overlay.color,
    alignment: slide.alignment,
    badge: slide.badge,
    countdownEndsAt: slide.countdown?.endsAt,
    contentPosition: slide.contentPosition,
    visibleDesktop: slide.visibility?.desktop,
    visibleMobile: slide.visibility?.mobile,
    scheduleStart: slide.schedule?.start,
    scheduleEnd: slide.schedule?.end,
  }));

  const ctx: StorefrontRuntimeContext = {
    identity: {
      slug: catalog.tenant.slug,
      tenant: catalog.tenant,
      storeInfo: { metodEntrega: catalog.config.metodEntrega, whatsappNumber: catalog.config.whatsappNumber, showAddressOnStorefront: catalog.config.showAddressOnStorefront !== false },
      locations: catalog.locations,
      mainLocation,
      socialLinks: catalog.socialLinks,
    },
    catalog: {
      categories: catalog.categories,
      products,
      combos: catalog.combos,
      bundles: [],
      offers: [],
      onPromotionSelected: () => undefined,
    },
    hero: {
      title: projection.hero.title,
      subtitle: projection.hero.subtitle,
      showSubtitle: projection.hero.showSubtitle,
      imageUrl: projection.hero.imageUrl,
      videoUrl: projection.hero.videoUrl,
      slides: heroSlides,
      autoplay: projection.hero.autoplay,
      intervalMs: projection.hero.intervalMs,
      showDots: projection.hero.showDots,
      showArrows: projection.hero.showArrows,
      height: projection.hero.height,
    },
    filters: {
      search, setSearch, selectedCat, setSelectedCat, productSort, setProductSort, filteredProducts,
      hasActiveFilters, clearFilters: () => { setSearch(""); setSelectedCat(null); }, isBrowsingFiltered: hasActiveFilters,
    },
    cart: { addToCart, qtyInCart, updateQty, baseLineKey, requiresDetail },
    experience: { plp: projection.plp },
  };

  const headerCategories = filterCategoriesForNav(
    catalog.categories.map((category) => ({ ...category, experience: category.experience ?? DEFAULT_CATEGORY_EXPERIENCE })),
    "header",
  ).slice().sort((a, b) => Number(b.experience.featured) - Number(a.experience.featured) || a.sortOrder - b.sortOrder)
   .map((category) => ({ id: category.id, name: category.name, href: landingPath(catalog.tenant.slug, "category", category.slug) }));
  const headerBrands = Array.from(new Set(catalog.products.map((product) => product.brandName).filter(Boolean) as string[])).slice(0, 24)
    .map((name) => ({ name, href: landingPath(catalog.tenant.slug, "brand", slugifyName(name)) }));
  const headerCollections = Array.from(new Set(catalog.products.flatMap((product) => product.tags))).slice(0, 16)
    .map((tag) => ({ name: tag, href: landingPath(catalog.tenant.slug, "collection", slugifyName(tag)) }));

  const logoUrl = doc.brand.logo.light?.src || catalog.tenant.logoUrl;
  const isMobile = false;
  const content = (
    <div className="market-surface relative isolate min-h-screen" style={projection.brandStyle}>
      <div data-eb-section="announcement"><MarketAnnouncementBar announcement={projection.announcement} isMobile={isMobile} storeSlug={catalog.tenant.slug} /></div>
      <div data-eb-section="header">
        <MarketStoreHeader
          tenantName={doc.brand.name || catalog.tenant.name} logoUrl={logoUrl || null}
          cartCount={cartCount} cartTotal={cartTotal} search={search} onSearchChange={setSearch}
          onOpenCart={() => setBottomTab("cart")} onOpenSearch={() => { setBottomTab("search"); document.getElementById("catalog-search")?.focus(); }}
          accountHref={catalog.customerAccountsEnabled ? "#" : undefined}
          experience={projection.header} navigation={doc.navigation} categories={headerCategories}
          brands={headerBrands} collections={headerCollections} onSelectCategory={setSelectedCat}
          storeSlug={catalog.tenant.slug} showReservations={catalog.showReservations}
          showServiceBookings={catalog.showServiceBookings} showB2bPortal={catalog.showB2bPortal}
          activeB2bChannel={catalog.activeB2bChannel}
        />
      </div>
      <div data-eb-section="home" className={cn(projection.header.sticky && "pt-14 md:pt-16", "pb-24")}>
        <StorefrontRuntime composition={projection.composition} ctx={ctx} />
      </div>
      <div data-eb-section="footer">
        <MarketFooter
          tenantName={doc.brand.name || catalog.tenant.name} description={catalog.tenant.description}
          logoUrl={logoUrl || null} phone={catalog.tenant.phone} whatsappNumber={catalog.config.whatsappNumber}
          locations={catalog.locations} categories={catalog.categories} socialLinks={catalog.socialLinks}
          footer={doc.footer} showMap={projection.footer.showMap} showReservations={catalog.showReservations}
          showServiceBookings={catalog.showServiceBookings} showB2bPortal={catalog.showB2bPortal}
          activeB2bChannel={catalog.activeB2bChannel} storeSlug={catalog.tenant.slug}
        />
      </div>
      {doc.mobile.bottomNav.enabled ? <div data-eb-section="mobile-bottom-nav"><MarketBottomNav activeTab={bottomTab} cartCount={cartCount} cartTotal={cartTotal} onTabChange={setBottomTab} /></div> : null}
    </div>
  );

  return content;
}
